import React, { ElementType, ReactElement, ReactNode } from 'react'

import { Tooltip as MUITooltip, ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import { getDataTooltipSurface } from './dataSurface'

export interface TooltipProps {
  arrow?: boolean
  children: ReactElement
  component?: ElementType<any>
  disableMaxWidth?: boolean
  disableHoverListener?: boolean
  enterDelay?: number
  followCursor?: boolean
  onClose?: () => void
  onDragEnd?: () => void
  onDragStart?: () => void
  onMouseOver?: () => void
  onMouseOut?: () => void
  onOpen?: () => void
  open?: boolean
  placement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top'
  slotProps?: {
    popper?: object
  }
  sx?: object
  title: ReactNode

  /** `data` is the chart tooltip's light card, for figures, not hints. */
  variant?: 'data' | 'default'
}

/**
 * The Tooltip component display informative text
 * when users hover over, focus on, or tap an element.
 */

const Tooltip = React.forwardRef((props: TooltipProps, ref: any) => {
  const {
    disableMaxWidth,
    enterDelay,
    slotProps,
    sx,
    title,
    variant = 'default',
    ...rest
  } = props

  const isData = variant === 'data'
  const theme = getTheme(isData ? 'light' : 'dark')

  return (
    <MUITooltip
      enterDelay={enterDelay ?? 300}
      PopperProps={{
        sx: { zIndex: 9999 },
        onClick: (ev) => ev.stopPropagation()
      }}
      title={
        title ? <ThemeProvider theme={theme}>{title}</ThemeProvider> : undefined
      }
      slotProps={{
        popper: {
          ...slotProps?.popper,
          sx: {
            zIndex: 9999,
            pointerEvents: 'none'
          }
        },
        tooltip: {
          sx: {
            ...(isData
              ? { ...getDataTooltipSurface(), maxWidth: 'none' }
              : { bgcolor: 'black.main' }),
            pointerEvents: 'none',
            ...(disableMaxWidth ? { maxWidth: 'none' } : null)
          }
        }
      }}
      sx={{
        position: 'relative',
        ...sx
      }}
      {...rest}
      ref={ref}
    />
  )
})

Tooltip.displayName = 'Tooltip'
export default Tooltip
